import {
  getCheapestLeadInOffer,
  RoomWithLeadInOffers,
} from '../../utils/roomOptions/prices';
import { CTAComponentsProps, CTAProps, RoomOptionOffer } from './types';
import { getUnavailableVariant } from './utils';

const getUnavailableReason = (
  isPackagePageV3Enabled: boolean | undefined,
  exceedMaxOccupants: boolean,
) => {
  if (!isPackagePageV3Enabled) {
    return 'This room is not part of this package option';
  }
  if (exceedMaxOccupants) {
    return 'This room cannot accommodate the selected number of travellers';
  }
  return 'This room is not available on the selected travel dates';
};

const createRoomOptionCTA =
  <LeadInOffer, Offer>({
    LeadInOfferPrice,
    CTAButton,
    OfferPrice,
    Unavailable,
    usePackagePageV3,
    EnterDatesForPricing,
  }: CTAComponentsProps<LeadInOffer, Offer>) =>
  ({
    roomOption,
    isOriginAndPackageSelected,
    isPreSearch,
    isBestRates,
    isSelected,
    isMainRooms,
    route,
    onCtaClick,
    travellerCount,
  }: CTAProps<LeadInOffer, Offer>) => {
    const isPackagePageV3Enabled = usePackagePageV3 && usePackagePageV3();

    const maxOccupants = roomOption?.roomType?.maxOccupants;

    // *** PRE-SEARCH ***
    // Hasn't selected previous sections (package option + flying from)
    if (!isOriginAndPackageSelected) {
      return <CTAButton disabled text="Check availability" />;
    }

    /*
      We use this createRoomOptionCTA fn to create RoomOption CTA components in both the "Main Rooms"
      and "Other Rooms" section.
      In the "Main Rooms" section, we want to render the price that matches the package option that has
      been selected by the user (this will be roomOption.matchingLeadInOffer).
      And in the "Other Rooms" section, we want to render the cheapest price that matches the route the
      user has chosen, regardless of package option selected.
    */
    if (isPreSearch) {
      let validLeadInOffer;

      if (isMainRooms) {
        validLeadInOffer = route && roomOption?.matchingLeadInOffer;
      } else {
        validLeadInOffer = getCheapestLeadInOffer(
          route,
          roomOption as RoomWithLeadInOffers,
        );
      }

      // When PackagePageV3 is enabled, default to showing offer for selected package,
      // but if it doesn't exist show the offer for any other package.
      const offer = isPackagePageV3Enabled
        ? validLeadInOffer
        : roomOption.matchingLeadInOffer;

      if (isBestRates) {
        return (
          <CTAButton
            onClick={() =>
              onCtaClick('CHANGE_DATES', { ctaText: 'Check availability' })
            }
            text="Check availability"
          />
        );
      }

      if (
        isPackagePageV3Enabled &&
        !isBestRates &&
        !offer &&
        EnterDatesForPricing
      ) {
        return (
          <>
            <EnterDatesForPricing reason="Enter travel dates to see pricing for this room" />
            <CTAButton
              onClick={() =>
                onCtaClick('CHANGE_DATES', { ctaText: 'Check availability' })
              }
              text="Check availability"
            />
          </>
        );
      }

      if (!!offer) {
        return (
          <>
            <LeadInOfferPrice
              leadInOffer={offer as LeadInOffer}
              route={route}
            />
            <CTAButton
              onClick={() =>
                onCtaClick('CHANGE_DATES', { ctaText: 'Check availability' })
              }
              text="Check availability"
            />
          </>
        );
      }
    }

    // *** POST-SEARCH ***
    // Room option matches selected package option OR selected package option is best rates
    if (roomOption.matchingLeadInOffer || isBestRates) {
      if (roomOption.matchingOffer) {
        return (
          <>
            <OfferPrice
              offer={
                roomOption.matchingOffer as Offer & RoomOptionOffer<LeadInOffer>
              }
            />
            {!isSelected && (
              <CTAButton
                onClick={() =>
                  onCtaClick('SELECT_ROOM', { ctaText: 'Select room' })
                }
                text="Select room"
              />
            )}
          </>
        );
      }

      const { ctaButtonText, unavailableText, ctaEvent } =
        getUnavailableVariant({
          maxOccupants,
          travellerCount,
        });
      return (
        <>
          <Unavailable reason={unavailableText} />
          <CTAButton
            onClick={() => onCtaClick(ctaEvent, { ctaText: ctaButtonText })}
            text={ctaButtonText}
          />
        </>
      );
    }

    if (isPackagePageV3Enabled && roomOption?.offers) {
      const getAvailableOffers = roomOption?.offers?.filter(Boolean);

      if (getAvailableOffers.length) {
        const getCheapestOffer = getAvailableOffers.reduce((accum, cur) => {
          return cur.charges.total < accum.charges.total ? cur : accum;
        });

        return (
          <>
            <OfferPrice
              offer={getCheapestOffer as Offer & RoomOptionOffer<LeadInOffer>}
            />
            <CTAButton
              onClick={() => {
                onCtaClick('SELECT_ROOM', { ctaText: 'Select room' });
              }}
              text="Select room"
            />
          </>
        );
      }
    }

    const exceedMaxOccupants =
      (travellerCount ?? 0) > (roomOption.roomType.maxOccupants ?? 0);

    return (
      <>
        <Unavailable
          reason={getUnavailableReason(
            isPackagePageV3Enabled,
            exceedMaxOccupants,
          )}
        />
        {isPackagePageV3Enabled ? (
          exceedMaxOccupants ? (
            <CTAButton
              onClick={() =>
                onCtaClick('CHANGE_TRAVELLERS', {
                  ctaText: 'Change travellers',
                })
              }
              text="Change travellers"
            />
          ) : (
            <CTAButton
              onClick={() =>
                onCtaClick('CHANGE_DATES', { ctaText: 'Change dates' })
              }
              text="Change dates"
            />
          )
        ) : (
          <CTAButton
            onClick={() =>
              onCtaClick('CHANGE_PACKAGE_OPTION', {
                ctaText: 'Change package option',
              })
            }
            text="Change package option"
          />
        )}
      </>
    );
  };

export default createRoomOptionCTA;
